import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SectionHeading } from '../components/SectionHeading';
import { ReadingCard } from '../components/ReadingCard';
import { PricingCard } from '../components/PricingCard';
import { BookingForm } from '../components/BookingForm';
import { FaqList } from '../components/FaqList';
import { ClientProof } from '../components/ClientProof';
import { NeedFromAbby } from '../components/NeedFromAbby';
import { TickDivider } from '../components/TickDivider';
import { Dial } from '../components/Dial';
import { usePageMeta } from '../hooks/usePageMeta';
import { membershipPlans, readings } from '../data/readings';
import { faqItems } from '../data/faq';
import type { ReadingId } from '../types/content';
const STEPS = [{
  n: 'I',
  title: 'Choose your reading',
  body: 'Birth Chart, Synastry or Future Forecast.'
}, {
  n: 'II',
  title: 'Share your details',
  body: 'Your birth date, exact time and city.'
}, {
  n: 'III',
  title: 'Abby studies your chart',
  body: 'About an hour of preparation before you meet.'
}, {
  n: 'IV',
  title: 'Meet by phone or video',
  body: 'A one-hour session, worldwide.'
}];
function isReadingId(v: string | null): v is ReadingId {
  return v === 'birth-chart' || v === 'synastry' || v === 'transits';
}
export function Readings() {
  usePageMeta('Vedic Astrology Readings with Abby', 'Book a Birth Chart, Synastry or Future Forecast reading with Vedic astrologer Abby. One-hour sessions by phone or video, worldwide.');
  const [params] = useSearchParams();
  const initial = params.get('type');
  const [readingType, setReadingType] = useState<ReadingId | ''>(isReadingId(initial) ? initial : '');
  useEffect(() => {
    const t = params.get('type');
    if (isReadingId(t)) setReadingType(t);
  }, [params]);
  function handleBook(id: ReadingId) {
    setReadingType(id);
    document.getElementById('book')?.scrollIntoView({
      block: 'start'
    });
  }
  return <>
      <section aria-labelledby="readings-title" className="dusk-field" data-id="element-855" data-mp="19q6kzod1ndtn">
        <div className="gutter grid items-center gap-12 py-[clamp(4rem,9vw,10rem)] lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]" data-id="element-856" data-mp="1eorbgw2huj4w">
          <div data-id="element-857" data-mp="1jnkpqqplue64">
            <p className="t-label flex items-center gap-3 text-graphite" data-id="element-858" data-mp="1jn2lb6ssgtn4">
              <span className="t-numeral text-gold-deep" data-id="element-859" data-mp="1of2u7oqu6wvu">I</span>
              <span aria-hidden="true" className="text-gold" data-id="element-860" data-mp="1ogdwtl8jpyld">
                ·
              </span>
              Readings with Abby
            </p>
            <h1 id="readings-title" className="t-display mt-6 max-w-[14ch]" data-id="element-861" data-mp="1xe7qeoexee4u">
              Your whole chart, not just your sun sign.
            </h1>
            <p className="t-lead mt-8 max-w-[54ch] text-graphite" data-id="element-862" data-mp="22chnj4k7not3">
              A true reading needs your whole chart, not just your sun sign. Abby spends about an hour studying your chart before you meet,
              then you have a one-hour session together by phone or video.
            </p>
          </div>
          <Dial wordmark className="mx-auto hidden w-full max-w-[32rem] lg:block" data-id="element-863" data-mp="2q9nqk5oyggvl" />
        </div>
      </section>

      <section aria-labelledby="choose-title" className="gutter section" data-id="element-864" data-mp="2lifef1w8qheq">
        <SectionHeading id="choose-title" numeral="II" label="Choose your reading" title="Three ways in." action={<ClientProof index={1} data-id="element-866" data-mp="2ci95ltbhm5lw" />} data-id="element-865" data-mp="2lh4dd9u6jnrb" />
        <ul className="mt-[clamp(3rem,5vw,6rem)] grid gap-[clamp(1rem,1.6vw,2rem)] lg:grid-cols-3" data-id="element-867" data-mp="2cjbujvp91xz6">
          {readings.map(r => <li key={r.id} data-id="element-868" data-mp="397qfyllf0ftx">
              <ReadingCard reading={r} onBook={handleBook} data-id="element-869" data-mp="356y5xq4py6wl" />
            </li>)}
        </ul>
        <div className="mt-6" data-id="element-870" data-mp="307lawi3p1tix">
          <NeedFromAbby data-id="element-871" data-mp="306sdw8ujeidz">confirm pricing ($200 placeholder)</NeedFromAbby>
        </div>
      </section>

      <section id="memberships" aria-labelledby="memberships-title" className="section gutter scroll-mt-24 bg-cream" data-id="element-872" data-mp="2v7p8gnanaa5h">
        <SectionHeading id="memberships-title" numeral="III" label="Memberships & Bundles" title="A reading every season." intro="Your chart stays the same, but the sky keeps moving. Abby's ideal rhythm is a reading every quarter, so you're always a step ahead of your timing." data-id="element-873" data-mp="1ou9doq2fw95q" />
        <ul className="mt-[clamp(3rem,5vw,6rem)] grid items-stretch gap-[clamp(1rem,1.6vw,2rem)] lg:grid-cols-3" data-id="element-874" data-mp="2ypldn2yzsxje">
          {membershipPlans.map(p => <li key={p.id} data-id="element-875" data-mp="33p7mz1bp9tmx">
              <PricingCard plan={p} data-id="element-876" data-mp="33mbonbvxx2xy" />
            </li>)}
        </ul>
      </section>

      <section aria-labelledby="how-title" className="gutter section" data-id="element-877" data-mp="3rc65e4jdrbnh">
        <SectionHeading id="how-title" numeral="IV" label="How it works" title="From your chart to your session." data-id="element-878" data-mp="3vfc5svjta8m1" />
        <div className="mt-[clamp(3rem,5vw,6rem)]" data-id="element-879" data-mp="3hfuk4p1tbbgw">
          <TickDivider data-id="element-880" data-mp="3meecb7701jhp" />
          <ol className="grid gap-10 pt-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-881" data-mp="3mdw9frpu070t">
            {STEPS.map(s => <li key={s.n} data-id="element-882" data-mp="25zmh6migxy4p">
                <span className="t-numeral text-[clamp(2rem,1.5rem+1.5vw,3.5rem)] font-light leading-none text-gold-deep" data-id="element-883" data-mp="2b00qs8zpxg4u">{s.n}</span>
                <h3 className="t-h3 mt-5" data-id="element-884" data-mp="23fq4s6ujsmvy">{s.title}</h3>
                <p className="mt-2 max-w-[30ch] text-graphite" data-id="element-885" data-mp="23icby5dj10dn">{s.body}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <section id="book" aria-labelledby="book-title" className="gutter section scroll-mt-20 border-t border-stone" data-id="element-886" data-mp="2cg4udiejoo59">
        <div className="grid gap-[clamp(3rem,6vw,9rem)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)]" data-id="element-887" data-mp="27imibj58dst1">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-888" data-mp="1jt2052gwizkt">
            <p className="t-label flex items-center gap-3 text-graphite" data-id="element-889" data-mp="1frq4ogbcjk4p">
              <span className="t-numeral text-gold-deep" data-id="element-890" data-mp="1tibs8ibqwi8x">V</span>
              <span aria-hidden="true" className="text-gold" data-id="element-891" data-mp="1tjmu2clmrfxe">
                ·
              </span>
              Book
            </p>
            <h2 id="book-title" className="t-h2 mt-5 max-w-[12ch]" data-id="element-892" data-mp="111r8uk79kkse">
              Book your reading.
            </h2>
            <p className="mt-6 max-w-[42ch] text-graphite" data-id="element-893" data-mp="w3ha5zmbz221">
              Share your details and we'll confirm a time that suits you. Sessions are held by phone or video, worldwide.
            </p>
            <dl className="mt-10 space-y-4 border-t border-stone pt-6 text-sm" data-id="element-894" data-mp="313qv6qwmms8t">
              <div className="flex justify-between gap-4" data-id="element-895" data-mp="35v8tf779meq3">
                <dt data-id="element-896" data-mp="35vsc4q4qp756">Session</dt>
                <dd className="text-charcoal" data-id="element-897" data-mp="3au3ksxjatblt">60 min, phone or video</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-898" data-mp="3avwi3m80ujh5">
                <dt data-id="element-899" data-mp="2d7mnvteh900l">Chart preparation</dt>
                <dd className="text-charcoal" data-id="element-900" data-mp="2i5ehcrursu6w">About 60 min</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-901" data-mp="2i7ikv8orbr6n">
                <dt data-id="element-902" data-mp="2n3fyv8cjslg9">Investment</dt>
                <dd className="text-charcoal" data-id="element-903" data-mp="2n6lqau8nfzf5">$200</dd>
              </div>
            </dl>
            <ClientProof index={2} className="mt-10" data-id="element-904" data-mp="2r7fd1kacv86g" />
          </div>
          <BookingForm readingType={readingType} onReadingTypeChange={setReadingType} data-id="element-905" data-mp="2d8ibj6ext89" />
        </div>
      </section>

      <section aria-labelledby="faq-title" className="gutter section bg-cream" data-id="element-906" data-mp="6eaic285m9ba">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)] lg:gap-[clamp(3rem,6vw,9rem)]" data-id="element-907" data-mp="1qwurhiep26io">
          <div data-id="element-908" data-mp="1mxm2sryigytz">
            <p className="t-label flex items-center gap-3 text-graphite" data-id="element-909" data-mp="1mx44lpsagajf">
              <span className="t-numeral text-gold-deep" data-id="element-910" data-mp="1i4b1f6cgxa9g">VI</span>
              <span aria-hidden="true" className="text-gold" data-id="element-911" data-mp="1d6rfdk9j3t6y">
                ·
              </span>
              Questions
            </p>
            <h2 id="faq-title" className="t-h2 mt-5" data-id="element-912" data-mp="188zlwlt8jz0n">
              Good to know.
            </h2>
          </div>
          <FaqList items={faqItems} data-id="element-913" data-mp="1370i5atl3036" />
        </div>
      </section>
    </>;
}